/**
 * useRefreshPhotoMetadataGraphQL hook
 *
 * GraphQL mutation hook for re-extracting EXIF metadata from the original photo file.
 * Used by the Photo Inspector dev tool to fix photos that didn't get their EXIF during upload.
 */

import { gql, useMutation } from '@apollo/client';
import type { Photo } from '@/types/photo';

const REFRESH_PHOTO_METADATA_MUTATION = gql`
  mutation RefreshPhotoMetadata($input: RefreshPhotoMetadataInput!) {
    refreshPhotoMetadata(input: $input) {
      id
      globalId
      title
      metadata {
        camera
        lens
        iso
        aperture
        shutterSpeed
        focalLength
        dateTaken
      }
    }
  }
`;

interface RefreshPhotoMetadataInput {
  photoId: string;
}

interface RefreshPhotoMetadataResult {
  refreshPhotoMetadata: Photo;
}

export function useRefreshPhotoMetadataGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    RefreshPhotoMetadataResult,
    { input: RefreshPhotoMetadataInput }
  >(REFRESH_PHOTO_METADATA_MUTATION);

  const refreshMetadata = async (photoId: string) => {
    const result = await mutate({
      variables: {
        input: { photoId },
      },
    });
    return result.data?.refreshPhotoMetadata ?? null;
  };

  return {
    refreshMetadata,
    loading,
    error,
  };
}
